import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";

// Slider
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Material UI Icons
import PersonIcon from '@mui/icons-material/Person';
import StraightenIcon from '@mui/icons-material/Straighten';
import GroupsIcon from '@mui/icons-material/Groups';
import FlagIcon from '@mui/icons-material/Flag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScaleIcon from '@mui/icons-material/Scale';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

// Components
import Navbar from "../../components/Navbar";
import ReservationCalendar from "../../components/ReservationCalendar";
import NotificationModal from "../AdminPanel/components/NotificationModal";

function YachtInfoScreen() {
    const [yacht, setYacht] = useState(null);
    const [availabilityDates, setAvailabilityDates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [buttonText, setButtonText] = useState("Rezervasyon Yap");
    const [isWarning, setIsWarning] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState({
        show: false,
        type: 'success',
        title: '',
        message: ''
    });
    const { id } = useParams();
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    // Ana slider için ayarları güncelliyoruz
    const mainSliderSettings = {
        dots: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,
        fade: true,
        arrows: true,
        autoplay: true,
        autoplaySpeed: 3000,
        pauseOnHover: true,
        cssEase: "ease-in-out",
        lazyLoad: true,
        adaptiveHeight: true,
    };

    let mainSlider = null;

    // Yat tipini sayıdan metne çeviren fonksiyon
    const getYachtTypeText = (typeNumber) => {
        const types = {
            1: "Motor Yacht",
            2: "Sailing Yacht", 
            3: "Catamaran",
            4: "Luxury Yacht",
            5: "Super Yacht",
        };
        return types[typeNumber] || "Bilinmiyor";
    };

    useEffect(() => {
        // Kullanıcı bilgilerini al
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                setCurrentUser(JSON.parse(userData));
            } catch (error) {
                console.error('Kullanıcı verisi parse hatası:', error);
            }
        }

        const fetchYachtDetails = async () => {
            try {
                console.log("Fetching yacht with id:", id);

                // localStorage'dan token'ı al
                const token = localStorage.getItem('authToken');

                if (!token) {
                    console.error('Kullanıcı girişi yapılmamış');
                    setError('Yat detaylarını görüntülemek için giriş yapmanız gerekiyor.');
                    setLoading(false);
                    return;
                }

                // Yat detaylarını ve müsaitlik bilgilerini paralel olarak çek
                const [yachtResponse, availabilityResponse] = await Promise.all([
                    fetch(`/rest/Yacht/getYachtById?id=${id}`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    }),
                    fetch(`/rest/api/yachtavailability/yacht/${id}`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    })
                ]);

                if (!yachtResponse.ok) {
                    if (yachtResponse.status === 401) {
                        console.error('Token geçersiz veya süresi dolmuş');
                        setError('Oturumunuzun süresi dolmuş. Lütfen tekrar giriş yapın.');
                        // Kullanıcıyı login sayfasına yönlendir
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('user');
                        setTimeout(() => {
                            window.location.href = '/login';
                        }, 2000);
                        setLoading(false);
                        return;
                    }
                    throw new Error(`HTTP error! status: ${yachtResponse.status}`);
                }

                const yachtData = await yachtResponse.json();
                console.log("Fetched yacht details:", yachtData);
                setYacht(yachtData);

                // Müsaitlik verilerini işle
                if (availabilityResponse.ok) {
                    const availabilityData = await availabilityResponse.json();
                    console.log("Fetched availability data:", availabilityData);
                    setAvailabilityDates(availabilityData);
                } else {
                    console.warn("Müsaitlik verileri yüklenemedi:", availabilityResponse.status);
                    setAvailabilityDates([]);
                }

                setLoading(false);

            } catch (error) {
                console.error("Fetch error:", error);
                setError(error.message || 'Yat detayları yüklenirken bir hata oluştu.');
                setLoading(false);
            }
        };

        fetchYachtDetails();
    }, [id]);

    // CSS sınıfları için yardımcı fonksiyon
    const getButtonClasses = () => {
        const baseClasses =
            "w-full mt-6 py-4 px-6 bg-gradient-to-r text-white rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 text-lg font-semibold";
        return `${baseClasses} ${
            isWarning
                ? "bg-gradient-to-r from-blue-600 to-blue-400 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                : "bg-gradient-to-r from-blue-600 to-blue-500 hover:shadow-blue-500/40 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
        } animate-fade-in`;
    };

    const handleReservationClick = () => {
        if (selectedStartDate && selectedEndDate) {
            const startDateStr = format(selectedStartDate, "yyyy-MM-dd");
            const endDateStr = format(selectedEndDate, "yyyy-MM-dd");
            navigate(
                `/reservation/${yacht.id}?startDate=${startDateStr}&endDate=${endDateStr}`
            );
        } else {
            setIsWarning(true);
            setButtonText("Lütfen Tarih Seçiniz");

            // Force animation restart
            const button = document.querySelector("button");
            button.style.animation = "none";
            // Trigger reflow
            void button.offsetHeight;
            button.style.animation = null;

            setTimeout(() => {
                setButtonText("Rezervasyon Yap");
                setIsWarning(false);

                // Force animation restart again
                button.style.animation = "none";
                // Trigger reflow
                void button.offsetHeight;
                button.style.animation = null;
            }, 2000);
        }
    };

    // ReservationCalendar'dan seçilen tarihleri almak için
    const handleDateSelect = (startDate, endDate) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        if (startDate && endDate) {
            setButtonText("Rezervasyon Yap");
            setIsWarning(false);
        }
    };

    // Notification modalını göster
    const showModal = (type = 'success', title, message) => {
        setShowNotificationModal({
            show: true,
            type,
            title,
            message
        });
    };

    // Notification modalını kapat
    const hideNotificationModal = () => {
        setShowNotificationModal({
            show: false,
            type: 'success',
            title: '',
            message: ''
        });
    };

    // Yat silme
    const handleDeleteYacht = async () => {
        try {
            setDeleting(true);

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch(`/rest/Yacht/deleteYacht/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Yat silinirken hata oluştu");
            }

            showModal('success', 'Başarılı!', 'Yat başarıyla silindi!');
            setTimeout(() => {
                navigate("/yachts");
            }, 1500);
        } catch (error) {
            console.error("Yat silme hatası:", error);
            showModal('error', 'Hata!', error.message || 'Yat silinirken bir hata oluştu');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    // Yat sahibi kontrolü
    const isYachtOwner = () => {
        return currentUser && yacht && currentUser.id === yacht.userId;
    };

    if (loading)
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                        <div className="text-white/60 font-light tracking-wider">
                            Yatınızı getiriyoruz...
                        </div>
                    </div>
                </div>
            </div>
        );

    if (error) {
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <div className="text-white/60">Hata: {error}</div>
                </div>
            </div>
        );
    }

    if (!yacht)
        return (
            <div className="min-h-screen relative">
                <div
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(/images/yacht-bg.jpg)`,
                        filter: "brightness(0.9)",
                    }}
                />
                <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />
                <div className="relative z-10 min-h-screen flex items-center justify-center">
                    <div className="text-white/60">Yat bulunamadı</div>
                </div>
            </div>
        );

    return (
        <div className="min-h-screen relative">
            {/* Background Image */}
            <div
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url(/images/yacht-bg-3.jpg)`,
                    filter: "brightness(0.9)",
                }}
            />

            {/* Gradient Overlay */}
            <div className="fixed inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" />

            {/* Content */}
            <div className="relative z-10">
                {/* <AuthButtons /> */}
                <Navbar />

                <div className="max-w-7xl mx-auto px-4 pt-32 pb-16">
                    {/* Header with Back Button and Delete Button */}
                    <div className="flex justify-between items-center mb-8">
                        <Link
                            to="/yachts"
                            className="inline-flex items-center text-white/60 hover:text-white transition-colors"
                        >
                            <svg
                                className="w-5 h-5 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                            Tüm Tekneler
                        </Link>
                        
                        {/* Admin Panel Button - Only show if user is yacht owner */}
                        {isYachtOwner() && (
                            <button
                                onClick={() => window.location.href = `/admin/yacht-details/${yacht.id}`}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                className={`inline-flex items-center px-3 py-2 bg-white/5 backdrop-blur-md border border-white/10 text-white/70 rounded-xl text-sm transition-all duration-300 gap-2 ${
                                    isHovered
                                        ? "bg-white/10 text-white border-white/20"
                                        : ""
                                }`}
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                                Yatınızı düzenlemek için tıklayın
                            </button>
                        )}
                    </div>

                    {/* Yacht Info */}
                    <div className="bg-white/5 backdrop-blur-md rounded-3xl overflow-hidden border border-white/10">
                        {/* Ana Slider */}
                        <div className="relative">
                            <Slider
                                {...mainSliderSettings}
                                ref={(slider) => (mainSlider = slider)}
                                className="yacht-main-slider"
                            >
                                {yacht.yachtImages && yacht.yachtImages.length > 0 ? (
                                    yacht.yachtImages.map((imageObj, index) => (
                                        <div
                                            key={imageObj.id}
                                            className="relative h-[600px]"
                                        >
                                            <img
                                                src={`https://cyprusyachtclub.com/rest-images/YachtImages/${imageObj.imageUrl}`}
                                                alt={`${yacht.name} - ${index + 1}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.src = '/images/yacht-bg.jpg'; // Fallback image
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                            <div className="absolute bottom-6 left-6">
                                                <span className="px-3 py-1 bg-black/50 backdrop-blur-md rounded-full text-white/90 text-sm">
                                                    {getYachtTypeText(yacht.type)}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    // Resim yoksa placeholder
                                    <div className="relative h-[600px]">
                                        <div className="w-full h-full bg-gradient-to-br from-blue-600/20 to-blue-800/20 flex items-center justify-center">
                                            <div className="text-center text-white/60">
                                                <svg className="w-32 h-32 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M3 13h1l1 2h2l1-2h2l1 2h2l1-2h2l1 2h2l1-2h2l1 2h1v1c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1v-1z"/>
                                                    <path d="M12 2l-1 3v6h2V5l-1-3zm4 8l-1-3v6h2V8l-1-2zm-8 0l-1-2v4h2V8l-1-2z"/>
                                                    <path d="M12 10v3"/>
                                                </svg>
                                                <p className="text-xl">{yacht.name}</p>
                                                <p className="text-sm text-white/40">Resim bulunamadı</p>
                                            </div>
                                        </div>
                                        <div className="absolute bottom-6 left-6">
                                            <span className="px-3 py-1 bg-black/50 backdrop-blur-md rounded-full text-white/90 text-sm">
                                                {getYachtTypeText(yacht.type)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </Slider>

                            {/* Slider Navigation Buttons */}
                            <button
                                onClick={() => mainSlider.slickPrev()}
                                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur-sm text-white/90 hover:bg-black/70 transition-all duration-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                            </button>
                            <button
                                onClick={() => mainSlider.slickNext()}
                                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur-sm text-white/90 hover:bg-black/70 transition-all duration-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                {/* Sol Kolon */}
                                <div className="space-y-6">
                                    <h1 className="text-4xl font-bold text-white mb-4">
                                        {yacht.name}
                                    </h1>

                                    {/* Ana Özellikler */}
                                    <div className="space-y-4 text-white/60">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                                                <PersonIcon className="text-blue-400" fontSize="small" />
                                            </div>
                                            <div>
                                                <p className="text-white/40 text-sm">
                                                    Kaptan
                                                </p>
                                                <p className="text-white">
                                                    {yacht.captain}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                                                <StraightenIcon className="text-blue-400" fontSize="small" />
                                            </div>
                                            <div>
                                                <p className="text-white/40 text-sm">
                                                    Uzunluk
                                                </p>
                                                <p className="text-white">
                                                    {yacht.length}m
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                                                <GroupsIcon className="text-blue-400" fontSize="small" />
                                            </div>
                                            <div>
                                                <p className="text-white/40 text-sm">
                                                    Kapasite
                                                </p>
                                                <p className="text-white">
                                                    {yacht.capacity} misafir
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Teknik Özellikler */}
                                    <div className="bg-white/5 rounded-2xl p-6 space-y-4">
                                        <h3 className="text-lg font-semibold text-white">
                                            Teknik Özellikler
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4 text-white/60">
                                            <div className="flex items-center gap-3">
                                                <DirectionsBoatIcon className="text-blue-400" fontSize="small" />
                                                <div>
                                                    <p className="text-white/40 text-sm">
                                                        Model
                                                    </p>
                                                    <p>{yacht.model || "Belirtilmemiş"}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <CalendarTodayIcon className="text-blue-400" fontSize="small" />
                                                <div>
                                                    <p className="text-white/40 text-sm">
                                                        Yıl
                                                    </p>
                                                    <p>{yacht.year}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <ScaleIcon className="text-blue-400" fontSize="small" />
                                                <div>
                                                    <p className="text-white/40 text-sm">
                                                        Gross Ton
                                                    </p>
                                                    <p>{yacht.grossTone || "Belirtilmemiş"}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <BuildIcon className="text-blue-400" fontSize="small" />
                                                <div>
                                                    <p className="text-white/40 text-sm">
                                                        Motor
                                                    </p>
                                                    <p>{yacht.engineInformation || "Belirtilmemiş"}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* İletişim */}
                                    <div className="bg-white/5 rounded-2xl p-6 space-y-4">
                                        <h3 className="text-lg font-semibold text-white">
                                            İletişim
                                        </h3>
                                        <div className="space-y-3 text-white/60">
                                            <div className="flex items-center gap-3">
                                                <FlagIcon className="text-blue-400" fontSize="small" />
                                                <span>{yacht.flag}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <LocationOnIcon className="text-blue-400" fontSize="small" />
                                                <span>{yacht.location || "Belirtilmemiş"}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <PhoneIcon className="text-blue-400" fontSize="small" />
                                                <span>{yacht.phone}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fiyat */}
                                    <div className="bg-white/5 rounded-2xl p-6 space-y-4">
                                        <h3 className="text-lg font-semibold text-white">
                                            Fiyat
                                        </h3>
                                        <div className="text-green-400 font-bold text-2xl">
                                            {new Intl.NumberFormat('tr-TR', {
                                                style: 'currency',
                                                currency: yacht.currency || 'TRY'
                                            }).format(yacht.price)}
                                        </div>
                                    </div>

                                    {/* Açıklama */}
                                    {yacht.description && yacht.description !== "Belirtilmemiş" && (
                                        <div className="bg-white/5 rounded-2xl p-6 space-y-4">
                                            <h3 className="text-lg font-semibold text-white">
                                                Açıklama
                                            </h3>
                                            <p className="text-white/60 leading-relaxed">
                                                {yacht.description}
                                            </p>
                                        </div>
                                    )}

                                    {/* Özellikler */}
                                    {yacht.features && yacht.features !== "Belirtilmemiş" && (
                                        <div className="bg-white/5 rounded-2xl p-6 space-y-4">
                                            <h3 className="text-lg font-semibold text-white">
                                                Özellikler
                                            </h3>
                                            <p className="text-white/60 leading-relaxed">
                                                {yacht.features}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Sağ Kolon - Sadece Takvim */}
                                <div>
                                    <ReservationCalendar
                                        reservedDates={yacht.availabilities || []}
                                        availableDates={availabilityDates}
                                        onDateSelect={handleDateSelect}
                                        selectedStartDate={selectedStartDate}
                                        selectedEndDate={selectedEndDate}
                                    />
                                    <button
                                        onClick={handleReservationClick}
                                        className={`${getButtonClasses()} `}
                                    >
                                        {buttonText}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 max-w-md mx-4 shadow-2xl shadow-black/40">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/20 mb-4">
                                <svg
                                    className="h-6 w-6 text-red-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">
                                Yatı Sil
                            </h3>
                            <p className="text-white/80 mb-6">
                                <strong>{yacht?.name}</strong> adlı yatınızı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                            </p>
                            <div className="flex justify-center gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={deleting}
                                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors disabled:opacity-50"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleDeleteYacht}
                                    disabled={deleting}
                                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:bg-red-400 flex items-center gap-2"
                                >
                                    {deleting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Siliniyor...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                            Sil
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <NotificationModal
                show={showNotificationModal.show}
                type={showNotificationModal.type}
                title={showNotificationModal.title}
                message={showNotificationModal.message}
                onClose={hideNotificationModal}
                autoClose={true}
                duration={1500}
            />
        </div>
    );
}

export default YachtInfoScreen;
